import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { IndexSearch, type IndexHomeData, type IndexResults, type IndexScope } from '@carouselgg/nexus/index-search'
import { useAuthStore } from '../store'
import { useMyOrgs } from '../use-my-orgs'
import { useMyTeams } from '../use-my-teams'
import { fetchIndex, fetchIndexHome, forgetSearches, loadRecent, rememberSearch, spacesFrom } from '../index-search'

/** What AppBar hands its search slot. */
export interface AppIndexSearchProps {
  open: boolean
  onClose: () => void
  query: string
  onQueryChange: (query: string) => void
}

// Fast enough to feel live, slow enough not to query every keystroke.
const DEBOUNCE_MS = 150

interface Answer {
  query: string
  scope: IndexScope
  results: IndexResults
}

/**
 * The Carousel Index wired to the API and the router — what every app's nav
 * puts in AppBar's search slot. Nothing is fetched until it is opened.
 */
export function AppIndexSearch({ open, ...rest }: AppIndexSearchProps) {
  if (!open) return null
  return <OpenIndexSearch {...rest} />
}

function OpenIndexSearch({ onClose, query, onQueryChange }: Omit<AppIndexSearchProps, 'open'>) {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const orgs = useMyOrgs(isAuthenticated)
  const teams = useMyTeams(isAuthenticated)
  const [scope, setScope] = useState<IndexScope>('all')
  const [answer, setAnswer] = useState<Answer | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recent, setRecent] = useState(loadRecent)
  const [competitions, setCompetitions] = useState<Pick<IndexHomeData, 'live' | 'open'>>({ live: [], open: [] })

  useEffect(() => {
    let live = true
    fetchIndexHome()
      .then((home) => { if (live) setCompetitions(home) })
      // The opening screen still works without them — recents and your
      // spaces — so a failure here leaves those two sections on their own.
      .catch(() => undefined)
    return () => { live = false }
  }, [])

  const q = query.trim()
  useEffect(() => {
    if (!q) return
    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      setPending(true)
      fetchIndex(q, scope, ctrl.signal)
        .then((results) => {
          setAnswer({ query: q, scope, results })
          setError(null)
        })
        .catch((e: unknown) => {
          if (!ctrl.signal.aborted) setError(e instanceof Error ? e.message : 'Search failed')
        })
        .finally(() => { if (!ctrl.signal.aborted) setPending(false) })
    }, DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [q, scope])

  // Results for another tab would be laid out wrong; show "Searching…" until
  // this tab's answer lands. Results for an earlier keyword in the same tab
  // stay up while the next one loads, so the screen doesn't flash.
  const results = answer && answer.scope === scope ? answer.results : null

  return (
    <IndexSearch
      open
      onClose={onClose}
      query={query}
      onQueryChange={onQueryChange}
      scope={scope}
      onScopeChange={setScope}
      results={results}
      loading={pending}
      error={q ? error : null}
      home={{ ...competitions, recent, spaces: spacesFrom(orgs, teams) }}
      onNavigate={(href) => {
        // Saved now, not in a state updater: navigating closes the Index,
        // and an updater queued on an unmounting component never runs.
        setRecent(rememberSearch(query, recent))
        navigate(href)
      }}
      onClearRecent={() => setRecent(forgetSearches())}
    />
  )
}
